import { useQuery } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Channel, ChannelSort, ConnectionOpen, Event, StreamChat, UserResponse } from 'stream-chat'

import { GET_WORKSPACE_MEMBERS } from '../data/queries/members'
import { useAuth } from '../hooks/use-auth'
import { useChatClient } from '../hooks/use-chat-client'
import { useUserQuery } from '../hooks/use-user-query'
import { useWorkspaceUsers } from '../hooks/use-workspace-users'
import type { IChatUser } from '../types/chat-types'
import type { IWorkspaceMember } from '../types/user'
import { MembershipStatus } from '../types/user'
import { isEmail, isPhone } from '../utils/common'
import { tokenUtils } from '../utils/token-utils'
import { createGenericContext } from './generic-context'

/**
 * Constants used throughout the chat context
 */
const CHAT_CONSTANTS = {
  MAX_BACKOFF_TIME: 30000,
  MAX_RECONNECT_ATTEMPTS: 2
} as const

/**
 * Chat state interface for managing related state together
 */
interface IChatState {
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error'
  unreadCount: number
  userConnection: ConnectionOpen | null
  workspaceUsers: (UserResponse & IChatUser)[]
}

/**
 * Chat context interface defining all available methods and properties
 */
interface IChatContext extends IChatState {
  chatClient: StreamChat
  disconnectUser: () => void
  isLoading: boolean
  mentionableUsers: UserResponse[]
  searchUser: (inputValue: string) => Promise<IChatUser[] | []>
  teamMembers: IWorkspaceMember[]
}

// Create context with type safety
const [useChat, ChatContextProvider] = createGenericContext<IChatContext>(
  'useChat must be used within the ChatContextProvider'
)

interface ChatProviderProps {
  children: React.ReactNode
}

/**
 * ChatProvider component that manages the Stream Chat connection lifecycle
 */
const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { selectedWorkspace, isWorkspacesLoading } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const { myDetails: userDetails } = useUserQuery()
  // Combined state management
  const [chatState, setChatState] = useState<IChatState>({
    connectionStatus: 'disconnected',
    unreadCount: 0,
    userConnection: null,
    workspaceUsers: []
  })
  const { client: chatClientService, addListeners, removeListeners } = useChatClient()

  const { refetch: fetchWorkspaceMembers } = useQuery<{
    workspaceMembers: IWorkspaceMember[]
  }>(GET_WORKSPACE_MEMBERS, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    skip: true,
    variables: {
      input: {
        membershipStatuses: [MembershipStatus.ACTIVE]
      }
    }
  })

  const { mentionableUsers, teamMembers, isLoading } = useWorkspaceUsers(
    userDetails,
    chatClientService,
    workspaceID,
    fetchWorkspaceMembers,
    isWorkspacesLoading
  )

  // Effect for chat client listeners
  useEffect(() => {
    addListeners()
    return () => removeListeners()
  }, [])

  const userChatToken = tokenUtils.getToken('User-Chat-Token')
  const reconnectAttempts = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Creates a standardized error handler for different contexts
   */
  const createErrorHandler = useCallback(
    (context: string) => (error: unknown) => {
      setChatState((prev) => ({ ...prev, connectionStatus: 'error' }))
      Sentry.captureException(error, {
        extra: {
          reconnectAttempts: reconnectAttempts.current,
          userDetails: {
            id: userDetails?.id,
            name: userDetails?.name
          }
        },
        tags: {
          component: 'ChatProvider',
          context,
          userId: userDetails?.id || 'unknown'
        }
      })
      console.error(`Chat connection error in ${context}:`, error)
    },
    [userDetails]
  )

  /**
   * Resets reconnection attempts and clears timeouts
   */
  const resetReconnectAttempts = useCallback(() => {
    reconnectAttempts.current = 0
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  /**
   * Schedules a reconnection attempt with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current < CHAT_CONSTANTS.MAX_RECONNECT_ATTEMPTS) {
      reconnectAttempts.current += 1

      const backoffTime = Math.min(Math.pow(2, reconnectAttempts.current) * 1000, CHAT_CONSTANTS.MAX_BACKOFF_TIME)

      Sentry.addBreadcrumb({
        category: 'chat',
        data: { backoffTime },
        level: 'info',
        message: `Scheduling reconnection attempt ${reconnectAttempts.current}/${CHAT_CONSTANTS.MAX_RECONNECT_ATTEMPTS}`
      })

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        if (chatState.connectionStatus !== 'connected') {
          connectUser()
        }
      }, backoffTime)
    } else {
      Sentry.captureMessage('Maximum Stream Chat reconnection attempts reached', {
        level: 'error',
        tags: {
          component: 'ChatProvider',
          userId: userDetails?.id || 'unknown'
        }
      })
    }
  }, [chatState.connectionStatus, userDetails])

  /**
   * Handles Stream Chat events
   */
  const handleStreamEvents = useCallback(
    (event: Event) => {
      if (event.total_unread_count !== null && event.total_unread_count !== undefined) {
        setChatState((prev) => ({ ...prev, unreadCount: event.total_unread_count || 0 }))
      }

      if (event.type === 'connection.changed') {
        if (!event.online) {
          Sentry.addBreadcrumb({
            category: 'chat',
            level: 'warning',
            message: 'Stream Chat connection went offline'
          })
          scheduleReconnect()
        } else {
          Sentry.addBreadcrumb({
            category: 'chat',
            level: 'info',
            message: 'Stream Chat connection restored'
          })
          resetReconnectAttempts()
        }
      }
    },
    [scheduleReconnect, resetReconnectAttempts]
  )

  /**
   * Connects user to Stream Chat
   */
  const connectUser = useCallback(async (): Promise<void> => {
    try {
      if (
        !chatState.userConnection &&
        chatState.connectionStatus === 'disconnected' &&
        userChatToken &&
        userDetails?.id
      ) {
        setChatState((prev) => ({ ...prev, connectionStatus: 'connecting' }))

        Sentry.addBreadcrumb({
          category: 'chat',
          data: {
            attemptNumber: reconnectAttempts.current + 1,
            userId: userDetails.id
          },
          level: 'info',
          message: 'Attempting to connect user to Stream Chat'
        })

        const connection = await chatClientService.connectUser(
          {
            id: userDetails.id,
            image: userDetails.photo || '',
            name: userDetails.name || ''
          },
          userChatToken
        )

        chatClientService.on(handleStreamEvents)

        // Monitor health check events
        chatClientService.on('health.check', (event: Event) => {
          Sentry.addBreadcrumb({
            category: 'chat',
            data: { connectionId: event.connection_id || 'unknown' },
            level: 'info',
            message: 'Health check completed'
          })
        })

        // Listen for connection errors
        chatClientService.on('connection.error', (event: Event & { error?: Error }) => {
          createErrorHandler('connection.error event')(event.error)
          scheduleReconnect()
        })

        if (connection) {
          setChatState((prev) => ({
            ...prev,
            connectionStatus: 'connected',
            unreadCount: connection?.me?.total_unread_count || 0,
            userConnection: connection
          }))

          resetReconnectAttempts()

          Sentry.addBreadcrumb({
            category: 'chat',
            data: {
              connectionId: String(connection?.connection_id || 'unknown'),
              userId: userDetails.id
            },
            level: 'info',
            message: 'Successfully connected to Stream Chat'
          })
        }
      }
    } catch (e) {
      console.error('Error connecting to Stream Chat', e)
      // Handle network change errors specifically
      Sentry.addBreadcrumb({
        category: 'chat',
        data: {
          error: 'ERR_NETWORK_CHANGED',
          userId: userDetails?.id
        },
        level: 'warning',
        message: 'Network changed detected during connection, will retry'
      })

      // For connection errors, let the existing reconnection logic handle it
      // since it already has exponential backoff
      scheduleReconnect()

      createErrorHandler('connectUser method')(e)
      scheduleReconnect()
    }
  }, [
    userChatToken,
    chatState.userConnection,
    userDetails,
    createErrorHandler,
    handleStreamEvents,
    scheduleReconnect,
    resetReconnectAttempts
  ])

  /**
   * Disconnects user from Stream Chat
   */
  const disconnectUser = useCallback((): void => {
    try {
      Sentry.addBreadcrumb({
        category: 'chat',
        data: { userId: userDetails?.id },
        level: 'info',
        message: 'Disconnecting user from Stream Chat'
      })

      chatClientService.disconnectUser()
      setChatState((prev) => ({
        ...prev,
        connectionStatus: 'disconnected',
        userConnection: null
      }))
      resetReconnectAttempts()
    } catch (e) {
      createErrorHandler('disconnectUser method')(e)
    }
  }, [userDetails, createErrorHandler, resetReconnectAttempts])

  /**
   * Queries messaging channels and filters users based on search input.
   * Handles errors gracefully by always returning an array (empty on error) to prevent UI blocking.
   */
  const queryAndFilterUsers = useCallback(
    async (inputValue: string, isRetry: boolean = false): Promise<IChatUser[]> => {
      // Guard against invalid userDetails.id to prevent sending empty $in to Stream
      if (!userDetails?.id) {
        return []
      }

      // Query channels with a LIMIT to prevent rate limits (aligned with backend PR #7947)
      const filters = {
        members: { $in: [userDetails.id] },
        type: 'messaging'
      }
      const sort: ChannelSort = { last_message_at: -1 as const }
      const options = { limit: 30 }

      let channels: Channel[]
      try {
        channels = await chatClientService.queryChannels(filters, sort, options)
      } catch (error) {
        // Check if this is a network change error that should be retried
        const isNetworkError = error instanceof Error && error.message.includes('ERR_NETWORK_CHANGED')

        if (isNetworkError && !isRetry) {
          // Log breadcrumb and retry once
          Sentry.addBreadcrumb({
            category: 'chat',
            level: 'warning',
            message: 'Network changed detected, attempting retry'
          })

          // Add a small delay before retry
          await new Promise((resolve) => setTimeout(resolve, 500))

          // Retry once with isRetry flag
          return queryAndFilterUsers(inputValue, true)
        }

        // For non-network errors or failed retries, capture exception with appropriate context
        Sentry.captureException(error, {
          tags: {
            context: isRetry ? 'searchUser method retry' : 'searchUser method'
          },
          extra: {
            filters,
            inputValue,
            options,
            sort,
            userId: userDetails?.id
          }
        })

        // Return empty array to prevent UI blocking
        return []
      }

      const lowercasedInput = inputValue.toLowerCase()
      const isEmailSearch = isEmail(inputValue)
      const isPhoneSearch = isPhone(inputValue)

      return channels.flatMap((channel: Channel) => {
        const connectedUserID = Object.keys(channel.state.members).find((key) => key !== userDetails.id)

        if (!connectedUserID) return []

        const userData = channel.state.members[connectedUserID].user as IChatUser
        if (userData?.deleted) return []

        if (isEmailSearch && userData?.email === inputValue) return [userData]
        if (isPhoneSearch && userData?.phone === inputValue) return [userData]
        if (userData?.name?.toLowerCase().includes(lowercasedInput)) return [userData]

        return []
      })
    },
    [chatClientService, userDetails.id]
  )

  /**
   * Optimized search user implementation using queryChannels with pagination.
   * This aligns with backend PR #7947 which changed from bulk queryChannels to
   * paginated channel queries to avoid rate limits.
   *
   * @param inputValue - The search input (name, email, or phone)
   * @returns Promise<IChatUser[]> - Array of matching users from existing channels
   */
  const searchUser = useCallback(
    async (inputValue: string): Promise<IChatUser[]> => {
      if (!inputValue.trim()) {
        return []
      }

      // queryAndFilterUsers handles all errors gracefully and returns empty array on failure
      return await queryAndFilterUsers(inputValue, false)
    },
    [queryAndFilterUsers]
  )

  // Effect for connection management
  useEffect(() => {
    const cleanup = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    if (userChatToken && userDetails?.id) {
      connectUser()
    }

    return () => {
      cleanup()
      if (chatState.userConnection && !userChatToken && userDetails?.id) {
        disconnectUser()
      }
    }
  }, [userChatToken, userDetails?.id, connectUser, disconnectUser, chatState.userConnection])

  const contextValue: IChatContext = {
    ...chatState,
    chatClient: chatClientService,
    disconnectUser,
    isLoading,
    mentionableUsers,
    searchUser,
    teamMembers
  }

  return <ChatContextProvider value={contextValue}>{children}</ChatContextProvider>
}

export { ChatContextProvider, ChatProvider, useChat }
